/**
 * Nearest Store Widget - Simple, clean design
 * Shows stores immediately, geolocation is optional bonus
 */

class NearestStoreWidget extends HTMLElement {
  constructor() {
    super();
    this.stores = [];
    this.translations = {};
  }

  connectedCallback() {
    this.loadData();
    this.bindEvents();
    // No auto-prompt. If the visitor already shared their location earlier this
    // session (e.g. on the store locator page), reuse it silently — otherwise
    // wait for them to tap "Use my location".
    this.useCachedLocation();
  }

  /**
   * Silently sort by a fresh cached location if one exists. Never prompts.
   */
  useCachedLocation() {
    const cached = this.getCachedLocation();
    if (cached) {
      this.sortByLocation(cached.lat, cached.lng);
    }
  }

  /**
   * Shared session cache (same key as the full store locator) so a location
   * granted on one page carries to the other without prompting again.
   */
  getCachedLocation() {
    try {
      const cached = sessionStorage.getItem('store-locator-location');
      if (!cached) return null;
      const { lat, lng, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < 600000) return { lat, lng };
    } catch (e) {
      // ignore cache errors
    }
    return null;
  }

  cacheLocation(lat, lng) {
    try {
      sessionStorage.setItem('store-locator-location', JSON.stringify({
        lat,
        lng,
        timestamp: Date.now()
      }));
    } catch (e) {
      // ignore storage errors
    }
  }

  /**
   * Compute distances from the given location, sort nearest-first and update UI.
   */
  sortByLocation(userLat, userLng) {
    const storesWithDistance = this.stores.map(store => ({
      ...store,
      distance: this.calculateDistance(userLat, userLng, store.lat, store.lng)
    })).sort((a, b) => a.distance - b.distance);

    this.updateStoreDistances(storesWithDistance);
  }

  loadData() {
    // Load store data
    const storeDataEl = this.querySelector('[data-store-data]');
    if (storeDataEl) {
      try {
        this.stores = JSON.parse(storeDataEl.textContent);
      } catch (e) {
        console.error('Failed to parse store data:', e);
      }
    }

    // Load translations
    const translationsEl = this.querySelector('[data-translations]');
    if (translationsEl) {
      try {
        this.translations = JSON.parse(translationsEl.textContent);
      } catch (e) {
        console.error('Failed to parse translations:', e);
      }
    }
  }

  bindEvents() {
    const geoBtn = this.querySelector('[data-geo-btn]');
    if (geoBtn) {
      geoBtn.addEventListener('click', () => this.findNearest());
    }
  }

  async findNearest() {
    const geoBtn = this.querySelector('[data-geo-btn]');
    const geoText = this.querySelector('[data-geo-text]');

    if (!navigator.geolocation) {
      return;
    }

    // Show loading state
    geoBtn.classList.add('is-loading');
    geoBtn.disabled = true;
    if (geoText) {
      geoText.textContent = this.translations.finding || 'Finder...';
    }

    try {
      const position = await this.getPosition();
      const userLat = position.coords.latitude;
      const userLng = position.coords.longitude;

      // Cache and sort (shared with the auto-locate path)
      this.cacheLocation(userLat, userLng);
      this.sortByLocation(userLat, userLng);

      // Reset button
      geoBtn.classList.remove('is-loading');
      geoBtn.disabled = false;
      if (geoText) {
        geoText.textContent = this.translations.useLocation || 'Find nærmeste';
      }

    } catch (error) {
      console.error('Geolocation error:', error);
      geoBtn.classList.remove('is-loading');
      geoBtn.disabled = false;
      if (geoText) {
        geoText.textContent = this.translations.useLocation || 'Find nærmeste';
      }
    }
  }

  getPosition() {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      });
    });
  }

  calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371; // Earth's radius in km
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  toRad(deg) {
    return deg * (Math.PI / 180);
  }

  updateStoreDistances(storesWithDistance) {
    const storesContainer = this.querySelector('.nearest-store-widget__stores');
    if (!storesContainer) return;

    // Map the rendered store elements by id (matched against the GID strings)
    const elements = Array.from(this.querySelectorAll('[data-store-id]'));
    const byId = new Map(elements.map(el => [String(el.dataset.storeId), el]));

    // Clear previous nearest markers + labels
    elements.forEach(el => {
      el.classList.remove('is-nearest');
      const oldLabel = el.querySelector('.nearest-store-widget__nearest-label');
      if (oldLabel) oldLabel.remove();
    });

    // Reorder every store nearest-first, set its distance, and mark the closest
    storesWithDistance.forEach((storeData, index) => {
      const el = byId.get(String(storeData.id));
      if (!el) return;

      const distanceEl = el.querySelector('[data-distance]');
      if (distanceEl) {
        distanceEl.textContent = this.formatDistance(storeData.distance);
        distanceEl.hidden = false;
      }

      // Re-append in sorted order (nearest first)
      storesContainer.appendChild(el);

      // Mark + label the closest store
      if (index === 0) {
        el.classList.add('is-nearest');
        const info = el.querySelector('.nearest-store-widget__store-info');
        if (info && !info.querySelector('.nearest-store-widget__nearest-label')) {
          const label = document.createElement('span');
          label.className = 'nearest-store-widget__nearest-label';
          label.textContent = this.translations.nearest || 'Nærmest dig';
          info.insertBefore(label, info.firstChild);
        }
      }
    });

    // Scroll the list back to the top so the nearest store is in view
    storesContainer.scrollTop = 0;
  }

  formatDistance(km) {
    if (km < 1) {
      const meters = Math.round(km * 1000);
      return `${meters} m`;
    }
    const rounded = Math.round(km * 10) / 10;
    const template = this.translations.distanceKm || '__DISTANCE__ km';
    return template.replace('__DISTANCE__', rounded);
  }
}

// Register custom element
if (!customElements.get('nearest-store-widget')) {
  customElements.define('nearest-store-widget', NearestStoreWidget);
}
